import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import TariffModal from './TariffModal';
import { useTranslation } from 'react-i18next';
import tariffsData from '../data/tariffs.json';
import CheckIcon from './icons/CheckIcon';
import PackageCard from './PackageCard';

const TariffsPageWrapper = styled.div`
  /* Add custom styles to match the HTML design */
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background: white;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const BenefitBox = styled.div`
  padding: 2rem;
  
  .benefit-icon {
    font-size: 2.5rem;
    color: var(--primary-orange);
    margin-bottom: 1rem;
    display: block;
  }
  
  .benefit-text {
    font-size: 1.2rem;
    font-weight: 600;
    color: #333;
  }
`;

const ServicesContainer = styled.div`
  /* Use flexbox so the last/second row is centered regardless of item count */
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 2rem;
  align-items: stretch;
  justify-content: center;
`;

// Sticky bar for the Manual/Automatic switcher (tabs)
// Keeps the switcher visible while scrolling the packages section
const StickyTabsBar = styled.div`
  position: sticky;
  /* Adjust these offsets if your site header height differs */
  top: 70px;
  z-index: 10;
  background: #ffffff;
  padding: 0.5rem 0;

  @media (max-width: 768px) {
    top: 60px;
  }
`;

// Constrain the width of the loose prices (individual lessons) table
// so it visually matches the packages row width.
// Assumptions based on PackageCard width (320px) and gap (24px / 1.5rem):
// - Desktop: typically 3 cards per row → 3*320 + 2*24 = 1008px
// - Tablet: typically 2 cards per row → 2*320 + 1*24 = 664px
// - Mobile: full width of container
const PricesTableWrapper = styled.div`
  margin: 2rem auto 0; /* space above, centered */
  width: 100%;
  max-width: 1008px; /* desktop default (≈3 cards row) */

  @media (max-width: 1199px) {
    max-width: 1008px; /* keep until we drop to 2 per row */
  }

  @media (max-width: 991px) {
    max-width: 664px; /* ≈2 cards row on tablet */
  }

  @media (max-width: 768px) {
    max-width: none; /* mobile uses container width */
  }
`;

// Match Auto page card styles
const AutoLikePackageCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(236, 92, 44, 0.2);
  }
`;

const AutoLikeCardHeader = styled.div`
  background: linear-gradient(45deg, var(--primary-orange), #ff7043);
  color: white;
  padding: 1.5rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
`;

const AutoLikeCardBody = styled.div`
  padding: 2rem;
`;

const AutoLikePrice = styled.p`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-orange);
  text-align: center;
  margin-bottom: 1.5rem;
`;

const AutoLikeFeatureList = styled.div`
  margin-bottom: 2rem;
`;

const AutoLikeFeatureItem = styled.p`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;

  svg {
    color: #28a745;
    margin-right: 0.75rem;
    font-size: 1.2rem;
    flex-shrink: 0;
  }
`;

const AutoLikeChooseButton = styled.button`
  width: 100%;
  background: var(--primary-blue);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--primary-orange);
    transform: translateY(-2px);
  }
`;

// Carousel styles for manual packages as a single scrollable row (no auto-move, no buttons)
const PackagesCarousel = styled.div`
  width: 100%;
  position: relative;
  margin-top: 2rem;
  padding: 0.5rem 0; /* a little breathing room */
`;

// Horizontally scrollable viewport
const PackagesViewport = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* smooth on iOS */
  scroll-behavior: smooth;
  padding-bottom: 0.25rem; /* space for potential scrollbar */

  /* Optional: subtle scrollbar styling; hidden on touch-only */
  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
  @media (hover: none) {
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const PackagesTrack = styled.div`
  display: flex;
  gap: 1.5rem;
  width: max-content; /* allow content to dictate width for horizontal scroll */
`;

// Full-bleed wrapper to allow content to stretch to the full viewport width,
// breaking out of the centered Container.
const FullBleed = styled.div`
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  width: 100vw;
`;

// Grid for manual packages that spans the full viewport width
const ManualPackagesGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  padding: 0 24px; /* keep some side breathing room on very large screens */
  box-sizing: border-box;

  /* Mobile default: single column */
  grid-template-columns: 1fr;

  /* Tablet: two per row */
  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Desktop: exactly three per row */
  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

// Note: CarouselPackageBox defined after PackageBox to avoid TDZ issues

const PackageBox = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #fcfcff 100%);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  width: 100%;
  max-width: 320px; /* narrower card */
  min-height: 420px; /* taller card */
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(236, 92, 44, 0.2);
  }
  
  .price-plan-box-content {
    padding: 2rem 1.5rem 1.25rem;
    text-align: center;
    flex: 1; /* stretch to make cards equal height */
    display: flex;
    flex-direction: column;
    
    .description {
      font-size: 2.1rem;
      font-weight: 800;
      color: #ec5c2c;
      margin-bottom: 1.25rem;
      letter-spacing: -0.02em;
    }
    
    .price-plan-container {
      display: flex;
      flex-direction: column;
      gap: 0.85rem;
    }
    
    .price-plan-card {
      display: flex;
      align-items: center;
      gap: 0.5rem;
        
      /* Replace legacy font icon with a modern CSS tick */
      .done-icon-container {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        color: var(--success, #28a745);
        position: relative;
      }
      .done-icon-container i { display: none; }
      .done-icon-container::before {
        content: '';
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid currentColor;
        box-sizing: border-box;
        display: inline-block;
      }
      .done-icon-container::after {
        content: '';
        position: absolute;
        width: 6px;
        height: 10px;
        border-right: 2px solid currentColor;
        border-bottom: 2px solid currentColor;
        transform: rotate(45deg);
        top: 4px;
        left: 7px;
      }
    }
  }
  
  .theorie-cursus-btn {
    padding: 1rem 1.5rem 1.5rem;
    border-top: 1px solid rgba(0,0,0,0.06);
    background: linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(250,250,255,0.9) 100%);
    
    .btn-choose-plan {
      background: var(--primary-blue);
      color: white;
      text-decoration: none;
      padding: 12px 20px;
      border-radius: 14px;
      font-weight: 700;
      letter-spacing: 0.01em;
      transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
      border: none;
      cursor: pointer;
      font-size: 0.975rem;
      width: 100%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 6px 14px rgba(0, 88, 180, 0.2);
      
      &:hover {
        background: var(--primary-orange);
        transform: translateY(-1px);
        box-shadow: 0 8px 18px rgba(236, 92, 44, 0.25);
      }
      
      &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(236, 92, 44, 0.3);
      }
    }
  }
`;

// Mobile horizontal card item wrapper
// Match Automatic card width on mobile by making each slide the same width
// as the centered Container (viewport minus 40px padding).
const MobilePackageItem = styled.div`
  min-width: calc(100vw - 40px);
  scroll-snap-align: start;
`;

// Small toggle button shown only on desktop/tablet
const ShowAllButton = styled.button`
  margin: 1rem auto 0;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 6px 12px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--primary-blue);
  background: #fff;
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(0,0,0,0.08);
  }

  @media (max-width: 768px) {
    display: none; /* hide on mobile */
  }
`;

// Use existing card visuals but ensure fixed width in the horizontal carousel
const CarouselPackageBox = styled(PackageBox)`
  flex: 0 0 320px; /* match max-width in PackageBox for consistent size */
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  .section-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--primary-blue);
  }
`;

const ToggleSection = styled.div`
  margin: 3rem 0;
  
  .tariff-header-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    cursor: pointer;
    
    h2 {
      font-size: 2rem;
      font-weight: 700;
      color: var(--primary-blue);
    }
    
    .toggle-icon {
      font-size: 2rem;
      color: var(--primary-orange);
    }
  }
  
  .loze-price-table-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    
    table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      transform: translateZ(0);
      will-change: transform, box-shadow, background-color;
        
      /* Keep currency and amount together */
      td.price-cell .nowrap {
        white-space: nowrap;
      }
      
      thead {
        background: linear-gradient(90deg, var(--primary-blue), #4b8eda);
        color: white;
      }
      
      th, td {
        padding: 1rem;
        text-align: left;
        border-bottom: 1px solid #dee2e6;
      }
      
      tbody tr {
        background-color: #ffffff;
        transition: background-color 0.25s ease, transform 0.2s ease, box-shadow 0.2s ease, opacity 0.6s ease;
        opacity: 0;
        animation: fadeInUp 600ms ease forwards;
      }

      /* Stagger the reveal slightly per row */
      tbody tr:nth-child(1) { animation-delay: 60ms; }
      tbody tr:nth-child(2) { animation-delay: 120ms; }
      tbody tr:nth-child(3) { animation-delay: 180ms; }
      tbody tr:nth-child(4) { animation-delay: 240ms; }
      tbody tr:nth-child(5) { animation-delay: 300ms; }
      tbody tr:nth-child(6) { animation-delay: 360ms; }
      tbody tr:nth-child(7) { animation-delay: 420ms; }
      tbody tr:nth-child(8) { animation-delay: 480ms; }

      tbody tr:hover {
        background-color: #f8fafc;
        transform: translateY(-2px);
        box-shadow: 0 6px 18px rgba(0,0,0,0.08);
      }
      
      strong {
        color: var(--primary-orange);
      }

      /* Badge to indicate row type (Lesson/Exam) in merged AUTO table */
      .type-badge {
        display: inline-block;
        margin-right: 0.5rem;
        padding: 0.15rem 0.5rem;
        font-size: 0.75rem;
        font-weight: 700;
        border-radius: 999px;
        letter-spacing: 0.02em;
        color: #fff;
        vertical-align: middle;
        white-space: nowrap;
      }
      .type-badge.lesson {
        background: #0d6efd; /* blue */
      }
      .type-badge.exam {
        background: #6f42c1; /* purple */
      }
    }
    /* Mobile: make the table the same width as a PackageCard (320px) and center it */
    @media (max-width: 768px) {
      /* simplify layout on mobile */
      display: block;

      > div {
        width: 320px; /* match PackageCard width */
        max-width: 100%;
        margin: 0 auto; /* center */
      }

      table {
        width: 320px; /* fixed to card width */
        max-width: 100%;
        margin: 0 auto; /* center the table */
        /* Make font sizes a bit smaller on mobile for better fit */
        font-size: 0.9rem;

        thead th {
          font-size: 1rem; /* slightly smaller heading on mobile */
          padding: 0.85rem 1rem;
        }

        tbody td {
          font-size: 0.9rem;
          padding: 0.75rem 1rem; /* tighten row padding on mobile */
        }

        .type-badge {
          font-size: 0.7rem; /* smaller badge text */
          padding: 0.1rem 0.45rem; /* proportionally smaller badge */
        }
      }
    }
  }
  
  @keyframes fadeInUp {
    from {
      transform: translateY(6px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

// Tabs for switching Manual/Automatic individual price tables
const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(0,0,0,0.08);
  background: #fff;
  padding: 0.25rem;
  border-radius: 999px;
`;

const TabButton = styled.button<{ $active?: boolean }>`
  border: none;
  background: ${(p) => (p.$active ? 'var(--primary-blue)' : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : 'var(--primary-blue)')};
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.1s ease;
  &[aria-selected='true'] { box-shadow: 0 4px 10px rgba(0,88,180,0.15); }
  &:active { transform: translateY(1px); }
`;

const Tariffs: React.FC = () => {
  const [showPriceTable] = useState(true);
  // New: tab switcher for Packages section (Manual/Automatic)
  const [activePackagesTab, setActivePackagesTab] = useState<'manual' | 'automatic'>('manual');
  // Removed individual prices tab switcher; tables are shown under each packages view
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<{
    price: string;
    features: React.ReactNode[];
    sectionTitle: React.ReactNode;
  } | null>(null);
  const { t } = useTranslation();

  // Ensures currency and amount stay together and removes trailing ",-"
  const formatPrice = (raw: string): string => {
    if (!raw) return raw;
    let s = String(raw).trim();
    // Remove any trailing ",-" optionally followed by spaces
    s = s.replace(/,\-\s*$/g, '');
    // Normalize space after euro and make it non-breaking
    if (/^€/.test(s)) {
      s = s.replace(/^€\s*/, '€\u00A0');
    }
    return s;
  };

  const handlePackageSelect = (price: string, features: string[], sectionTitle: string) => {
    setSelectedPackage({ price, features, sectionTitle });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPackage(null);
  };

  // Renders the individual prices table for the given mode (manual | automatic)
  const renderIndividualTable = (mode: 'manual' | 'automatic') => {
    const lessons = tariffsData[mode].individual.lessons;
    const exams = tariffsData[mode].individual.exams;
    // Helper: remove duplicated type words from labels since we already show a type badge
    const sanitizeLabel = (raw: string, type: 'lesson' | 'exam') => {
      let s = raw || '';
      if (type === 'exam') {
        // Remove localized words for exam
        s = s.replace(/\b(exam|examen)\b/gi, '');
      } else if (type === 'lesson') {
        // Remove localized words for lesson
        s = s.replace(/\b(rijles|lesson|les)\b/gi, '');
      }
      // Cleanup extra spaces created by removals
      s = s.replace(/\s{2,}/g, ' ').trim();
      return s;
    };
    return (
      <ToggleSection>
        {showPriceTable && (
          <div className="loze-price-table-container" id={`individual-table-${mode}`}>
            <div>
              <table aria-label={`${t('tariffs.individual.heading', { defaultValue: 'Our Loose Prices' })} - ${t(`tariffs.${mode}.title`)}`}>
                <thead>
                  <tr>
                    <th colSpan={2} style={{ textAlign: 'center', fontSize: '1.1rem', letterSpacing: '0.02em' }}>
                      {t('tariffs.individual.heading', { defaultValue: 'Our Loose Prices' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ...lessons.map((it) => ({ ...it, type: 'lesson' as const })),
                    ...exams.map((it) => ({ ...it, type: 'exam' as const })),
                  ].map((row, i) => (
                    <tr key={`individual-${mode}-${row.type}-${i}`}>
                      <td>
                        <span className={`type-badge ${row.type}`}>
                          {row.type === 'lesson' ? t('tariffs.individual.lesson') : t('tariffs.individual.exam')}
                        </span>
                        {sanitizeLabel(String(t(row.labelKey)), row.type)}
                      </td>
                      <td className="price-cell"><span className="nowrap">{formatPrice(row.price)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ToggleSection>
    );
  };

  // NEW: responsive + show-all logic for Manual packages tab
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [showAllManual, setShowAllManual] = useState<boolean>(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply);
    };
  }, []);

  return (
    <TariffsPageWrapper>
      <BenefitsSection>
        <Container>
        </Container>
      </BenefitsSection>

      {/* Passenger Car Tariffs (Packages) with Manual/Automatic tabs */}
      <section id="passenger-car-tariffs">
        <Container>
          <SectionHeader>
            <StickyTabsBar>
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Tabs role="tablist" aria-label={`${t('tariffs.title') || 'Tariffs'} - packages`}>
                  <TabButton
                    role="tab"
                    aria-selected={activePackagesTab === 'manual'}
                    aria-controls="packages-section"
                    $active={activePackagesTab === 'manual'}
                    onClick={() => setActivePackagesTab('manual')}
                  >
                    {t('tariffs.manual.title')}
                  </TabButton>
                  <TabButton
                    role="tab"
                    aria-selected={activePackagesTab === 'automatic'}
                    aria-controls="packages-section"
                    $active={activePackagesTab === 'automatic'}
                    onClick={() => setActivePackagesTab('automatic')}
                  >
                    {t('tariffs.automatic.title')}
                  </TabButton>
                </Tabs>
              </div>
            </StickyTabsBar>
          </SectionHeader>

          <div id="packages-section">
            {activePackagesTab === 'manual' ? (
              <>
                  <ServicesContainer>
                      {tariffsData.manual.packages.map((pkg, idx) => {
                          // Build features: replace the "60 minutes" item with a Manual/Automatic label per requirements
                          const nonMinuteFeatures = pkg.features.filter((fk) => fk !== 'tariffs.packages.60_minutes');
                          const translatedFeatures = [
                            t('tariffs.manual.package.title'),
                            ...nonMinuteFeatures.map((fk) => t(fk)),
                          ];
                          const price = formatPrice(pkg.price);
                          // Determine creative package name per manual package by inferring lessons from feature keys
                          const lessonsFeature = pkg.features.find((fk) => /tariffs\.packages\.(\d+)_lessons/.test(fk));
                          const lessonsMatch = lessonsFeature ? lessonsFeature.match(/tariffs\.packages\.(\d+)_lessons/) : null;
                          const lessonsCount = lessonsMatch ? parseInt(lessonsMatch[1], 10) : undefined;
                          let creativeNameKey: string;
                          if (lessonsCount === 40) creativeNameKey = 'tariffs.manual.packages.name_40';
                          else if (lessonsCount === 30) creativeNameKey = 'tariffs.manual.packages.name_30';
                          else if (lessonsCount === 20) creativeNameKey = 'tariffs.manual.packages.name_20';
                          else if (lessonsCount === 15) creativeNameKey = 'tariffs.manual.packages.name_15';
                          else if (lessonsCount === 10) creativeNameKey = 'tariffs.manual.packages.name_10';
                          else if (lessonsCount === 5) creativeNameKey = 'tariffs.manual.packages.name_retake';
                          else creativeNameKey = 'tariffs.manual.package.title'; // fallback when not found
                          const creativeName = t(creativeNameKey);

                          return (
                              <PackageCard
                                  key={`manual-pkg-${idx}`}
                                  title={creativeName}
                                  price={price}
                                  features={translatedFeatures}
                                  buttonLabel={t('tariffs.packages.choose')}
                                  onChoose={() => handlePackageSelect(price, translatedFeatures, creativeName)}
                              />
                          );
                      })}
                  </ServicesContainer>
                <PricesTableWrapper>
                  {renderIndividualTable('manual')}
                </PricesTableWrapper>
              </>
            ) : (
              <>
                <ServicesContainer>
                  {tariffsData.automatic.packages.map((pkg, idx) => {
                    // Build features: replace the "60 minutes" item with a Manual/Automatic label per requirements
                    const nonMinuteFeatures = pkg.features.filter((fk) => fk !== 'tariffs.packages.60_minutes');
                    const translatedFeatures = [
                      t('tariffs.automatic.package.title'),
                      ...nonMinuteFeatures.map((fk) => t(fk)),
                    ];
                    const price = formatPrice(pkg.price);
                    // Determine creative package name per automatic package by inferring lessons from feature keys
                    const lessonsFeature = pkg.features.find((fk) => /tariffs\.packages\.(\d+)_lessons/.test(fk));
                    const lessonsMatch = lessonsFeature ? lessonsFeature.match(/tariffs\.packages\.(\d+)_lessons/) : null;
                    const lessonsCount = lessonsMatch ? parseInt(lessonsMatch[1], 10) : undefined;
                    let creativeNameKey: string;
                    if (lessonsCount === 40) creativeNameKey = 'tariffs.automatic.packages.name_40';
                    else if (lessonsCount === 30) creativeNameKey = 'tariffs.automatic.packages.name_30';
                    else if (lessonsCount === 20) creativeNameKey = 'tariffs.automatic.packages.name_20';
                    else if (lessonsCount === 10) creativeNameKey = 'tariffs.automatic.packages.name_10';
                    else creativeNameKey = 'tariffs.automatic.title'; // fallback when not found
                    const creativeName = t(creativeNameKey);

                    return (
                      <PackageCard
                        key={`automatic-pkg-${idx}`}
                        title={creativeName}
                        price={price}
                        features={translatedFeatures}
                        buttonLabel={t('tariffs.packages.choose')}
                        onChoose={() => handlePackageSelect(price, translatedFeatures, creativeName)}
                      />
                    );
                  })}
                </ServicesContainer>
                <PricesTableWrapper>
                  {renderIndividualTable('automatic')}
                </PricesTableWrapper>
              </>
            )}
          </div>
        </Container>
      </section>
      
      <TariffModal 
        isOpen={isModalOpen}
        onClose={closeModal}
        packageInfo={selectedPackage}
      />
    </TariffsPageWrapper>
  );
};

export default Tariffs;